import { ref, type Ref } from 'vue'
import { getUserMessageForRawError } from '@/features/crosschain-buy/buy-flow/presentation/errors'

interface ReadonlyValueRef<T> {
  readonly value: T
}

export type SourceConnectControl = 'wallet_panel' | 'primary'

interface Deps {
  uiError: Ref<string>
  sourceChainType: Ref<'evm' | 'solana'>
  phantomDeeplinkAvailable: ReadonlyValueRef<boolean>
  tonAddress: Ref<string>
  isTonBackendConnected: Ref<boolean>
  hasActiveTonConnectSession: ReadonlyValueRef<boolean>
  connectEvmWallet: () => Promise<unknown>
  disconnectEvmWallet: () => Promise<unknown>
  connectSolanaWallet: () => Promise<unknown>
  connectPhantomDeeplink: () => Promise<unknown>
  disconnectSolanaWallet: () => Promise<unknown>
  connectTonWallet: (address: string) => unknown
  disconnectTonWallet: () => void
  connectTon: () => Promise<boolean>
  recheckTonBackendSession: () => Promise<boolean>
  reconnectTonForFreshProof: () => Promise<boolean>
  disconnectTon: () => void
  recordDiagnostic: (event: string, data?: Record<string, unknown>) => void
}

export function useWalletHandlers(deps: Deps) {
  const {
    uiError,
    sourceChainType,
    phantomDeeplinkAvailable,
    tonAddress,
    isTonBackendConnected,
    hasActiveTonConnectSession,
    connectEvmWallet,
    disconnectEvmWallet,
    connectSolanaWallet,
    connectPhantomDeeplink,
    disconnectSolanaWallet,
    connectTonWallet,
    disconnectTonWallet,
    connectTon,
    recheckTonBackendSession,
    reconnectTonForFreshProof,
    disconnectTon,
    recordDiagnostic,
  } = deps

  const isSourceBusy = ref(false)
  const isTonBusy = ref(false)

  const friendlyWalletError = (error: unknown): string => getUserMessageForRawError(error)

  const clearStaleError = () => {
    uiError.value = ''
  }

  const handleConnectSolana = async () => {
    clearStaleError()
    isSourceBusy.value = true
    try {
      await connectSolanaWallet()
      uiError.value = ''
    } catch (error) {
      uiError.value = friendlyWalletError(error)
    } finally {
      isSourceBusy.value = false
    }
  }

  const handleDisconnectSolana = async () => {
    uiError.value = ''
    isSourceBusy.value = true
    try {
      await disconnectSolanaWallet()
    } catch (error) {
      uiError.value = error instanceof Error ? error.message : 'Failed to disconnect Solana wallet'
    } finally {
      isSourceBusy.value = false
    }
  }

  const handleConnectEvm = async () => {
    clearStaleError()
    isSourceBusy.value = true
    try {
      await connectEvmWallet()
      uiError.value = ''
    } catch (error) {
      uiError.value = friendlyWalletError(error)
    } finally {
      isSourceBusy.value = false
    }
  }

  const handleDisconnectEvm = async () => {
    uiError.value = ''
    isSourceBusy.value = true
    try {
      await disconnectEvmWallet()
    } catch (error) {
      uiError.value = error instanceof Error ? error.message : 'Failed to disconnect EVM wallet'
    } finally {
      isSourceBusy.value = false
    }
  }

  const handleConnectSource = async (control: SourceConnectControl = 'primary') => {
    if (isSourceBusy.value) return
    if (sourceChainType.value === 'solana') {
      if (!phantomDeeplinkAvailable.value) {
        await handleConnectSolana()
        return
      }
      recordDiagnostic('sourceWallet.connect.requested', { control })
      await handleConnectPhantomDeeplink()
    } else {
      await handleConnectEvm()
    }
  }

  // Explicit "Connect with Phantom" button on mobile web without an injected
  // provider. Navigates the tab into Phantom via our encrypted deeplink and
  // never resolves — `isSourceBusy` is only cleared on the synthetic
  // redirect_failed path (iOS PWA popup-blocked navigation), not on success.
  const handleConnectPhantomDeeplink = async () => {
    clearStaleError()
    isSourceBusy.value = true
    try {
      await connectPhantomDeeplink()
    } catch (error) {
      uiError.value = friendlyWalletError(error)
      isSourceBusy.value = false
    }
  }

  const handleDisconnectSource = async () => {
    if (sourceChainType.value === 'solana') {
      await handleDisconnectSolana()
    } else {
      await handleDisconnectEvm()
    }
  }

  const handleConnectTon = async () => {
    clearStaleError()
    isTonBusy.value = true
    try {
      if (tonAddress.value && isTonBackendConnected.value) {
        await connectTonWallet(tonAddress.value)
        uiError.value = ''
        return
      }

      const activeTonSession = hasActiveTonConnectSession.value
      const backendSessionRestored = activeTonSession ? await recheckTonBackendSession() : false
      if (backendSessionRestored && tonAddress.value) {
        await connectTonWallet(tonAddress.value)
        uiError.value = ''
        return
      }

      const connected = activeTonSession ? await reconnectTonForFreshProof() : await connectTon()
      if (connected && tonAddress.value) {
        await connectTonWallet(tonAddress.value)
        uiError.value = ''
      }
    } catch (error) {
      uiError.value = error instanceof Error ? error.message : 'Failed to connect TON wallet'
    } finally {
      isTonBusy.value = false
    }
  }

  const handleDisconnectTon = () => {
    uiError.value = ''
    isTonBusy.value = true
    try {
      disconnectTon()
      disconnectTonWallet()
    } catch (error) {
      uiError.value = error instanceof Error ? error.message : 'Failed to disconnect TON wallet'
    } finally {
      isTonBusy.value = false
    }
  }

  return {
    isSourceBusy,
    isTonBusy,
    handleConnectSource,
    handleDisconnectSource,
    handleConnectTon,
    handleDisconnectTon,
  }
}
